/**
 * Inspection Report tab (read-only display).
 *
 * The inspection report is CAPTURED exclusively through the `record_result`
 * workflow action (the "Record Result" dialog on the detail header) - that is
 * the single capture point for the outcome, the result reason, inspector
 * notes, the repair flag, and the digital report sections. This tab does NOT
 * capture anything; it renders the report that the action recorded so the
 * outcome can be reviewed after the fact.
 *
 * Data
 * ----
 * The report field schema (sections, options, per-option severity, grouping)
 * comes from `GET /inspections/{uuid}/report-metadata/`; the recorded values
 * come from the inspection's `attribute_values` plus the summary fields on the
 * inspection itself (`result`, `result_reason`, `inspector_notes`,
 * `repair_required`). Everything is data - nothing tenant-specific is
 * hardcoded here.
 *
 * Visibility
 * ----------
 * The detail page only mounts this tab once a result has been recorded (the
 * outcome states), so there is always a report to display when it renders.
 */

import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ClipboardCheck, Hash, ListChecks, MessageSquare } from 'lucide-react'

import { Badge } from '@/shared/components/ui/badge'
import { cn } from '@/shared/lib/utils'
import { normaliseInspectionReportField } from '@/shared/lib/forms'
import {
  useInspectionChoices,
  useInspectionReportMetadata,
  type ChoiceOption,
  type InspectionDetail,
  type InspectionReportField,
  type InspectionReportSeverity,
} from '@/admin/hooks/use-inspections'

const FAIL_WORKMANSHIP = 'fail_workmanship'

interface InspectionReportTabProps {
  uuid: string
  inspection: InspectionDetail
}

export function InspectionReportTab({
  uuid,
  inspection,
}: InspectionReportTabProps) {
  const {
    data: metadataFields,
    isLoading: metadataLoading,
    isError: metadataError,
  } = useInspectionReportMetadata(uuid, true)
  const { data: choices, isLoading: choicesLoading } = useInspectionChoices()

  if (metadataLoading || choicesLoading) {
    return (
      <div className="rounded-lg border border-[var(--rule)] bg-[var(--bg-card)] p-5">
        <p className="text-sm text-[var(--ink-3)]">Loading inspection report...</p>
      </div>
    )
  }

  if (metadataError) {
    return (
      <div className="rounded-lg border border-[var(--rule)] bg-[var(--bg-card)] p-5">
        <p className="text-sm text-[var(--ink-2)]">
          The inspection report could not be loaded. Refresh and try again.
        </p>
      </div>
    )
  }

  return (
    <InspectionReportDisplay
      inspection={inspection}
      metadataFields={metadataFields ?? []}
      resultOptions={choices?.results ?? []}
    />
  )
}

interface ReportSection {
  key: string
  title: string
  description: string
  icon: React.ReactNode
  fields: ReturnType<typeof normaliseInspectionReportField>[]
}

function InspectionReportDisplay({
  inspection,
  metadataFields,
  resultOptions,
}: {
  inspection: InspectionDetail
  metadataFields: InspectionReportField[]
  resultOptions: ChoiceOption[]
}) {
  const { t } = useTranslation()
  const values = inspection.attribute_values ?? {}

  const sections = useMemo<ReportSection[]>(() => {
    const normalised = metadataFields.map(normaliseInspectionReportField)
    const quantities = normalised.filter((f) => f.field_type === 'NUMBER')
    const assessments = normalised.filter((f) => f.field_type === 'MULTI_SELECT')
    const comments = normalised.filter(
      (f) => f.field_type !== 'NUMBER' && f.field_type !== 'MULTI_SELECT',
    )

    const built: ReportSection[] = []
    if (quantities.length > 0) {
      built.push({
        key: 'quantities',
        title: 'Quantities',
        description: 'Counts captured on the roof.',
        icon: <Hash size={16} />,
        fields: quantities,
      })
    }
    if (assessments.length > 0) {
      built.push({
        key: 'assessment',
        title: 'Assessment sections',
        description: 'Findings recorded during the inspection.',
        icon: <ListChecks size={16} />,
        fields: assessments,
      })
    }
    if (comments.length > 0) {
      built.push({
        key: 'comments',
        title: 'Additional comments',
        description: 'Anything else recorded.',
        icon: <MessageSquare size={16} />,
        fields: comments,
      })
    }
    return built
  }, [metadataFields])

  const resultLabel =
    resultOptions.find((o) => o.value === inspection.result)?.label ??
    (inspection.result ? humanise(inspection.result) : null)
  const repairRequired =
    inspection.repair_required || inspection.result === FAIL_WORKMANSHIP

  return (
    <div className="flex flex-col gap-4">
      {/* Inspection summary - the recorded outcome. */}
      <section className="flex flex-col gap-4 rounded-lg border border-[var(--rule)] bg-[var(--bg-card)] p-5">
        <div className="flex items-center gap-2">
          <span className="text-[var(--ink-3)]">
            <ClipboardCheck size={16} />
          </span>
          <div>
            <h3 className="text-sm font-medium text-[var(--ink)]">
              {t('inspections.report.summaryTitle', 'Inspection summary')}
            </h3>
            <p className="text-[11px] text-[var(--ink-3)]">
              {t(
                'inspections.report.summaryDescription',
                'The overall outcome recorded for this inspection.',
              )}
            </p>
          </div>
        </div>
        <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <DisplayRow label="Inspection result">
            {resultLabel ? (
              <Badge variant={inspection.outcome === 'fail' ? 'destructive' : 'secondary'}>
                {resultLabel}
              </Badge>
            ) : (
              <NotRecorded />
            )}
          </DisplayRow>
          <DisplayRow label="Repair required">
            <span className="text-sm text-[var(--ink)]">
              {repairRequired ? 'Yes' : 'No'}
            </span>
          </DisplayRow>
          {inspection.result_reason ? (
            <DisplayRow label="Result reason">
              <span className="text-sm text-[var(--ink)]">
                {humanise(inspection.result_reason)}
              </span>
            </DisplayRow>
          ) : null}
          {inspection.inspector_notes ? (
            <DisplayRow label="Inspector notes" full>
              <p className="text-sm whitespace-pre-wrap text-[var(--ink)]">
                {inspection.inspector_notes}
              </p>
            </DisplayRow>
          ) : null}
        </dl>
      </section>

      {sections.map((section) => (
        <section
          key={section.key}
          className="flex flex-col gap-4 rounded-lg border border-[var(--rule)] bg-[var(--bg-card)] p-5"
        >
          <div className="flex items-center gap-2">
            <span className="text-[var(--ink-3)]">{section.icon}</span>
            <div>
              <h3 className="text-sm font-medium text-[var(--ink)]">
                {section.title}
              </h3>
              <p className="text-[11px] text-[var(--ink-3)]">
                {section.description}
              </p>
            </div>
          </div>
          <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {section.fields.map((field) => (
              <DisplayRow
                key={field.code_name}
                label={field.display_name}
                full={field.field_type !== 'NUMBER'}
              >
                <FieldValue field={field} value={values[field.code_name]} />
              </DisplayRow>
            ))}
          </dl>
        </section>
      ))}
    </div>
  )
}

function DisplayRow({
  label,
  full,
  children,
}: {
  label: string
  full?: boolean
  children: React.ReactNode
}) {
  return (
    <div className={cn('flex flex-col gap-1', full && 'sm:col-span-2')}>
      <dt className="text-[11px] font-medium uppercase tracking-[0.04em] text-[var(--ink-3)]">
        {label}
      </dt>
      <dd>{children}</dd>
    </div>
  )
}

function NotRecorded() {
  return <span className="text-sm text-[var(--ink-3)]">Not recorded</span>
}

/**
 * Read-only rendering of a recorded report field value, keyed by field type.
 * MULTI_SELECT resolves each stored option value to its label + severity chip;
 * NUMBER / text pass through; empty values render a muted placeholder.
 */
function FieldValue({
  field,
  value,
}: {
  field: ReturnType<typeof normaliseInspectionReportField>
  value: unknown
}) {
  if (field.field_type === 'MULTI_SELECT') {
    const selected = Array.isArray(value) ? (value as string[]) : []
    if (selected.length === 0) {
      return <span className="text-sm text-[var(--ink-3)]">No findings recorded</span>
    }
    const options = field.options ?? []
    return (
      <ul className="flex flex-col gap-1.5">
        {selected.map((v) => {
          const option = options.find((o) => o.value === v)
          return (
            <li key={v} className="flex items-center gap-2">
              <span className="text-sm text-[var(--ink)]">
                {option?.label ?? humanise(v)}
              </span>
              <SeverityChip severity={option?.severity} />
            </li>
          )
        })}
      </ul>
    )
  }

  if (value == null || value === '') return <NotRecorded />

  return <span className="text-sm text-[var(--ink)]">{String(value)}</span>
}

/**
 * Finding severity chip mirroring the capture form. Red -> error token, amber
 * -> warn token; `none`/absent renders nothing. Tokens only.
 */
function SeverityChip({ severity }: { severity?: InspectionReportSeverity }) {
  if (severity !== 'red' && severity !== 'yellow') return null
  const isRed = severity === 'red'
  return (
    <span
      className={cn(
        'inline-flex h-[18px] shrink-0 items-center rounded-full px-2 text-[10px] font-medium uppercase tracking-[0.04em]',
        isRed
          ? 'bg-[var(--status-error-bg)] text-[var(--status-error)]'
          : 'bg-[var(--status-warn-bg)] text-[var(--status-warn)]',
      )}
    >
      {isRed ? 'Red' : 'Amber'}
    </span>
  )
}

/** snake_case / lower value -> Title Case label fallback. */
function humanise(value: string): string {
  return value
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}
